/**
 * createCrmObject - Shared utility for creating CRM objects with associations.
 *
 * Encapsulates the standard pattern used by all entity types:
 * 1. Separate properties from associations in form data
 * 2. Build an objectCreated event with properties
 * 3. Build associationAdded events for each association
 * 4. Submit the event batch
 *
 * Entity-specific callers can pre-process data (e.g. normalize legacy fields,
 * parse quantities) before passing it here.
 */

import { toast } from 'sonner';

import { logger } from '@/lib/logger';
import type { OverwriteTarget } from '@/types/crm';

import {
  applyOptimisticBatch,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';
import {
  appendDraftEvents,
  assertValidDraftEntities,
  type DraftEntity,
} from './draftEntities';
import { getUserFriendlyEventError } from './errors';
import { createEventBuilder, type EventBuilder } from './EventBuilder';
import { createEventsClient } from './EventsClient';
import {
  reconcileOptimisticCreate,
  reconcileOptimisticDrafts,
} from './reconcileOptimisticCreate';

export interface AssociationEntry {
  association_type_name: string;
  target_entity_id: string;
}

export interface CreateObjectResult {
  entityId: string;
}

/**
 * Create a CRM object from form data produced by CrmCreateForm.
 *
 * CrmCreateForm outputs `{ ...properties, associations?: AssociationEntry[] }`.
 * This function splits that into objectCreated + associationAdded events and
 * submits them as a single batch.
 *
 * Await-vs-fire-and-forget split: whether the returned promise waits on the
 * network is driven entirely by `data.draft_entities`.
 * - When `draft_entities` is non-empty (a nested create submitted alongside
 *   its not-yet-persisted drafts), the promise awaits `client.submitEvents`
 *   and rejects if it fails. The caller MUST handle that rejection (e.g.
 *   render the error inline) — nothing else will surface it.
 * - When `draft_entities` is absent or empty, submission is fire-and-forget:
 *   the promise resolves immediately with the optimistic `entityId`, and a
 *   failure later rolls back the optimistic batch and shows an error toast.
 *   This is the pre-existing (non-draft) contract, preserved byte-for-byte.
 * `assertValidDraftEntities` validates the draft list synchronously, before
 * either path — a validation failure throws immediately out of this
 * function rather than going through the rollback/toast flow above.
 *
 * @param orgId - Organization ID
 * @param entityTypeName - Entity type (e.g. 'ahoy_contact', 'org_project')
 * @param data - Form data with properties + optional associations key
 * @param userId - Current user ID (for event source)
 * @param options.extraAssociations - Additional associations to add (e.g. default pipeline stage)
 * @param options.onBuildEvents - Hook to add extra events to the builder before submission
 */
export async function createCrmObject(
  orgId: number,
  entityTypeName: string,
  data: Record<string, unknown>,
  userId: number,
  options?: {
    extraAssociations?: AssociationEntry[];
    onBuildEvents?: (builder: EventBuilder, entityId: string) => void;
  }
): Promise<CreateObjectResult> {
  const client = createEventsClient(orgId);
  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
  });
  const entityId = crypto.randomUUID();

  // Separate properties, associations, draft entities, and the batch-level
  // overwrite_targets. `overwrite_targets` MUST NOT fall through into
  // `properties` (it is not an entity property) — it is the parent form's
  // merged single-cardinality conflict acknowledgements (its own acked
  // conflicts plus each referenced draft's), and must be forwarded to the
  // event batch so the backend can remove the conflicting existing
  // associations before adding the new ones. Dropping it makes the backend
  // reject the whole atomic batch with a cardinality violation despite the
  // user's acknowledgement.
  const { associations, draft_entities, overwrite_targets, ...properties } =
    data;
  const overwriteTargets = overwrite_targets as OverwriteTarget[] | undefined;

  // Create object with properties only
  builder.objectCreated(
    entityId,
    entityTypeName,
    properties as Record<string, string>
  );

  // Add associations from form data
  const formAssociations = Array.isArray(associations)
    ? (associations as AssociationEntry[])
    : [];

  for (const assoc of formAssociations) {
    builder.associationAdded(
      entityId,
      entityTypeName,
      assoc.association_type_name,
      assoc.target_entity_id
    );
  }

  // Add any extra associations (e.g. default pipeline stage)
  if (options?.extraAssociations) {
    for (const assoc of options.extraAssociations) {
      // Skip if already provided by the form
      const alreadyInForm = formAssociations.some(
        fa => fa.association_type_name === assoc.association_type_name
      );
      if (!alreadyInForm) {
        builder.associationAdded(
          entityId,
          entityTypeName,
          assoc.association_type_name,
          assoc.target_entity_id
        );
      }
    }
  }

  // Deep clone the snapshot — submitEvents' 409-retry rebuild would replay
  // these drafts, so a shallow array copy could replay differently if a caller
  // mutated a draft's properties/associations between attempts.
  const drafts: DraftEntity[] = structuredClone(
    (draft_entities as DraftEntity[] | undefined) ?? []
  );
  // Allow callers to add extra events (rarely needed). Run this BEFORE
  // validating so the parent event count reflects everything the parent
  // actually emits, including hook-added events.
  options?.onBuildEvents?.(builder, entityId);

  // Parent event count = the exact number of events built so far (objectCreated
  // + the deduped form/extra associations + any onBuildEvents additions). Taken
  // from the builder rather than recomputed from the raw inputs, so deduplicated
  // extras aren't double-counted and hook-added events are included. Validated
  // before folding drafts or applying any optimistic state, so a rejection
  // surfaces to the caller without leaving partial store state.
  assertValidDraftEntities(drafts, builder.build().length);

  appendDraftEvents(builder, drafts);

  const events = builder.build();
  const context = applyOptimisticBatch(
    {
      events,
      ...(overwriteTargets?.length
        ? { overwrite_targets: overwriteTargets }
        : {}),
    },
    orgId
  );

  if (drafts.length > 0) {
    try {
      const response = await client.submitEvents(events, overwriteTargets);
      // Reconcile the committed parent and each folded draft with authoritative
      // server state, exactly as the service create paths do. The optimistic
      // entries hold only client-minted values; the WS echo of our own submit
      // arrives at the same seq the optimistic write already used, so the
      // store's seq guard skips it and server-normalized/generated fields
      // (display-name formula, normalized values, generated defaults) would
      // otherwise stay stale. Fire-and-forget: the create already committed,
      // and both helpers swallow their own errors (a hydrate failure is
      // non-fatal — the next WS event / refetch reconciles).
      void reconcileOptimisticCreate(orgId, entityTypeName, entityId, response);
      void reconcileOptimisticDrafts(orgId, drafts);
      return { entityId };
    } catch (err: unknown) {
      rollbackOptimisticBatch(context);
      logger.error(
        'Optimistic CRM object create (with drafts) failed; rolled back optimistic batch',
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'crm_events',
          entityType: entityTypeName,
          orgId,
          entityId,
          draftEntityIds: drafts.map(d => d.entity_id),
        }
      );
      throw err;
    }
  }

  void client.submitEvents(events, overwriteTargets).catch((err: unknown) => {
    rollbackOptimisticBatch(context);
    toast.error(getUserFriendlyEventError(err));
    logger.error(
      'Optimistic CRM object create failed; rolled back optimistic entry',
      err instanceof Error ? err : new Error(String(err)),
      { component: 'crm_events', entityType: entityTypeName, orgId, entityId }
    );
  });

  return { entityId };
}
